import type { AgeGroup } from "./store";

export type SearchParamsLike = {
  get(name: string): string | null;
};

function norm(value: string | null): string {
  return (value || "").trim().toLowerCase();
}

// Source belongs to the Meta family (Facebook / Instagram). Matches the canonical
// sources plus the partner/campaign-scoped variants we see in real traffic, e.g.
// "meta_ads_weareteachers", "meta-sitelink", "fb-featuredofferings". Token-bounded
// so unrelated sources like "fbvygueh" (an email source) or "metabolism" do NOT match.
function isMetaFamilySource(source: string): boolean {
  return /^(meta|facebook|instagram|fb|ig)([_-]|$)/.test(source);
}

/**
 * True when the landing URL indicates a paid Meta (Facebook/Instagram) ad arrival.
 *
 * We treat ANY Meta-family `utm_source` as paid: we only run paid campaigns on
 * Meta and never tag organic Meta traffic with a `utm_source`, so the presence of
 * a Meta source means an ad click. The `utm_medium` is intentionally ignored —
 * partners tag it inconsistently (`paid_social`, `social`, `an`, `fb`, empty, ...),
 * and we are not adding new acquisition channels, so supporting the Meta set well
 * is all we need.
 *
 * This stays safe for the real organic risk: a bare `fbclid` (which Facebook
 * appends to organic shares, including ones kids click) carries no `utm_source`,
 * so it does NOT match and the Meta Pixel does not load. Non-Meta sources never
 * match either, so paid Google traffic (`googleads|cpc`) does not load it.
 */
export function isMetaPaidUtm(searchParams: SearchParamsLike): boolean {
  return isMetaFamilySource(norm(searchParams.get("utm_source")));
}

export function shouldDisableAgeSegmentation(pathname: string | null | undefined): boolean {
  return pathname === "/embed" || pathname?.startsWith("/embed/") === true;
}

export function getAutoAgeGroupDecision(input: {
  searchParams: SearchParamsLike;
}): AgeGroup | null {
  const { searchParams } = input;

  // Marketing requirement: Meta (Facebook/Instagram) paid ad visitors landing on any
  // page should be treated as grownups (no popup). Pixel loading is separate
  // (`usePaidAdChannel`) and is not gated by this age mark.
  //
  // Detection:
  // - Primary: Meta arrival per `isMetaPaidUtm` (any Meta-family utm_source).
  // - Optional override: pm_auto_age=grownup
  const autoAge = norm(searchParams.get("pm_auto_age"));
  if (autoAge === "grownup") return "grownup";
  if (isMetaPaidUtm(searchParams)) return "grownup";

  return null;
}
